import { FC, Fragment, useCallback } from 'react';
import { useFieldArray, useFormContext, useWatch } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { useDebouncedSave } from 'hooks/useDebounceSave';

import { FieldTable2 } from 'components/_basic/Form/FieldTable2';
import { FormSectionCard } from 'components/_basic/FormSectionCard';
import { FormSectionFilledCard } from 'components/_basic/FormSectionFilledCard';
import { Loader } from 'components/_basic/Loader/Loader';
import { IPeriodsTableRow } from 'components/BusinessToolForm/constants/periods';
import {
  GLOBAL_PERIOD_NAME,
  GLOBAL_PERIOD_TABLE_HEADINGS_NAME,
} from 'components/BusinessToolForm/Sections/Global/form';
import { convertToOpposite, reCalculatedPeriodValues } from 'components/BusinessToolForm/utils/helpers';

import { getPeriodColumnsNames } from 'helpers/businessTool';
import { concatWithDot } from 'helpers/string';

import { ILoanRepaymentScheduleFormProps } from './types';
import { LOAN_REPAYMENT_SCHEDULE_NAMES } from './utils/form';
import { FullScreenWrapper } from '../../../components/FullScreenWrapper';
import { useTotalExpensesRecalculation } from '../../../hooks/useTotalExpensesRecalculation';
import { useUnitChange } from '../../../hooks/useUnitChange';
import { FINANCING_ACTIVITIES_NAMES } from '../../../ProjectedCashFlow/DirectForm/DirectFormComponents/Sections/FinancingActivities/utils/form';
import { financingActivitiesPrefix } from '../../../ProjectedCashFlow/utils/form';
import { FIRST_COLUMN_WIDTH, loanRepaymentSchedulePrefix } from '../../utils/form';
import { PeriodFormHeader } from '../components/PeriodFormHeader';

import styles from './LoanRepaymentScheduleForm.module.scss';

export const LoanRepaymentScheduleForm: FC<ILoanRepaymentScheduleFormProps> = ({ disabled }) => {
  const { t } = useTranslation();

  const { control, setValue, getValues } = useFormContext();

  const { key } = useUnitChange();

  const period = useWatch({ name: GLOBAL_PERIOD_NAME });

  const columnsNames = getPeriodColumnsNames(period);

  const tableName = concatWithDot(loanRepaymentSchedulePrefix, LOAN_REPAYMENT_SCHEDULE_NAMES.TABLE);

  const { fields, update } = useFieldArray({ control, name: tableName });

  const loanTable = useWatch({ name: tableName });

  const variantsName = concatWithDot(loanRepaymentSchedulePrefix, LOAN_REPAYMENT_SCHEDULE_NAMES.VARIANTS, ':index');

  const repaymentOfLoanField = concatWithDot(financingActivitiesPrefix, FINANCING_ACTIVITIES_NAMES.REPAYMENT_DEBT);

  const loanHandler = useCallback((tableValues: IPeriodsTableRow[]) => {
    const repaymentOfLoan = convertToOpposite(tableValues[0]);
    setValue(repaymentOfLoanField, repaymentOfLoan);
  }, []);

  useDebouncedSave({ data: loanTable, saveFunction: loanHandler, debounceTime: 500, skipExecutionOnFirstRender: true });

  const { recalculateHandler, isPending } = useTotalExpensesRecalculation();

  const recalculateLoanHandler = async (rowIndex: number, fieldName: string) => {
    const tableValues = getValues(tableName);
    if (rowIndex === 0) {
      const updatedPeriodValues = reCalculatedPeriodValues(fieldName, tableValues[rowIndex]);
      update(rowIndex, updatedPeriodValues);
    } else {
      await recalculateHandler(6, fieldName, tableValues[rowIndex], true);
    }
  };

  return (
    <FormSectionCard
      collapsible
      title={t('financialPlan.cashFlowElements.loanRepaymentSchedule.title')}
      disabled={disabled}
    >
      <FullScreenWrapper title={t('financialPlan.cashFlowElements.loanRepaymentSchedule.title')} disabled={disabled}>
        <Fragment key={key}>
          <PeriodFormHeader
            disabled={disabled}
            periodId="loanRepaymentScheduleFormHeader.period"
            currencyUnitName={concatWithDot(loanRepaymentSchedulePrefix, LOAN_REPAYMENT_SCHEDULE_NAMES.CURRENCY)}
          />
          <FormSectionFilledCard className={styles.contentWrapper}>
            {isPending && <Loader className={styles.loader} />}
            <FieldTable2
              name={tableName}
              headerName={GLOBAL_PERIOD_TABLE_HEADINGS_NAME}
              columnsNames={columnsNames || []}
              rows={fields}
              disabled={disabled}
              watchRowTitleName={variantsName}
              firstColumnWidth={FIRST_COLUMN_WIDTH}
              reCalculationHandler={recalculateLoanHandler}
              recalculateOnChangeRowIndexes={[0]}
            />
          </FormSectionFilledCard>
        </Fragment>
      </FullScreenWrapper>
    </FormSectionCard>
  );
};
